import React from 'react';
import { useRouter } from 'next/router';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { actionsGeneratorV2 } from '@docavenue/core';
import { NextSeo } from 'next-seo';
import InventoryPage from '@/components/templates/InventoryPage/InventoryPage';
import { useTranslation } from '@/src/i18n';
import InventorySection from '@/components/templates/InventoryPage/InventorySection';
import { APP_ROUTES } from '@/src/constants';
import { wrapper } from '@/src/store';
import waitSagaMiddleware from '@/src/ssr/waitSagaMiddleware';

type Props = {
  page: number;
  data: GetListApiResponse<CardHcd>;
};

const LIMIT_PER_PAGE = 90;

const action = actionsGeneratorV2({
  resource: 'directoryPractitioners',
  scope: 'pat-public',
  chunkUrlResource: 'directory/practitioners',
});

const PractitionersIndex = ({ page, data }: Props) => {
  const { t } = useTranslation('common');
  const breadcrumbs = [
    {
      name: 'Maiia',
      url: APP_ROUTES.HOME,
    },
    {
      name: t('directory_practitioners'),
      url: APP_ROUTES.PRACTITIONERS.INDEX,
    },
  ];
  const router = useRouter();
  const displayItems = data.items.map(p => ({
    card: p,
    url: p.url ?? APP_ROUTES.HOME,
  }));
  const showNext = page >= 1 && (page + 1) * LIMIT_PER_PAGE < data.total;
  const showPrev = page > 1;
  const onNext = () => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: (page + 1).toString(),
      },
    });
  };
  const onPrev = () => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: (page - 1).toString(),
      },
    });
  };
  return (
    <>
      <NextSeo
        title={t('seo-title_directory_practitioners')}
        description={t('seo-description_directory_practitioners')}
      />
      <InventoryPage
        breadcrumbs={breadcrumbs}
        showNext={showNext}
        showPrev={showPrev}
        onNext={onNext}
        onPrev={onPrev}
      >
        <InventorySection
          title="directory_practitioners"
          items={displayItems}
        />
      </InventoryPage>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps<Props>(
  store => async ({ query: { page: pageQs = '1' } }) => {
    const page = parseInt(pageQs as string, 10);
    if (page < 1) {
      return { props: { page, data: { items: [], total: 0 } } };
    }
    const data = await new Promise<GetListApiResponse<CardHcd>>(resolve => {
      const dispatchAction = action.getList({
        page: page - 1,
        limit: LIMIT_PER_PAGE,
        sort: 'professional.practitionerId',
        direction: 'desc',
      });
      (dispatchAction as any).onSuccess = resolve;
      (dispatchAction as any).onError = () => resolve({ items: [], total: 0 });
      store.dispatch(dispatchAction);
    });
    await waitSagaMiddleware(store);
    return { props: { page, data } };
  },
);

export default PractitionersIndex;
